import axios from 'axios';
import { useStore } from './store';
import type { Engine, PromptJob } from './store';
import type { FlowkitSkillProvenanceData } from './flowkitSkills';
import { stripCharacterUgcImages } from './characterUgc';

const api = axios.create({
  baseURL: '/api',
  timeout: 60000
});

api.interceptors.request.use(config => {
  const token = useStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  // A session belongs to this owner-controlled workstation. Background calls
  // may fail while Flowboard, Flowkit, TikTok, or another dependency recovers;
  // they must never discard Studio state or attached work. The explicit Sign
  // out control is the sole way to end a Studio session.
  err => Promise.reject(err)
);

export default api;

// ── Error normalisation ────────────────────────────────────────────────────
// API failures do not arrive in one shape: Express routes answer `{ error }`,
// the FastAPI services behind them (Flow Kit, Flowboard) answer `{ detail }`,
// and validation failures answer `{ detail: [{ loc, msg, type }, …] }`.
// Reading `data.error` straight into rendered state therefore sometimes put an
// object into JSX — the crash React reports as error #31 ("object with keys
// {detail}"), which is what the auto-merge status line hit. Route every API
// error through this so what reaches the UI is always a string.
const MESSAGE_KEYS = ['error', 'detail', 'message', 'msg', 'reason', 'error_message'];
const MAX_ERROR_LENGTH = 400;

function flattenErrorValue(value: unknown, seen: Set<object>): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'object') return '';

  if (seen.has(value as object)) return '';
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map(item => flattenErrorValue(item, seen)).filter(Boolean).join('; ');
  }

  const record = value as Record<string, unknown>;
  for (const key of MESSAGE_KEYS) {
    const nested = flattenErrorValue(record[key], seen);
    if (nested) return nested;
  }

  try {
    const json = JSON.stringify(value);
    return json && json !== '{}' ? json : '';
  } catch {
    return '';
  }
}

/**
 * Reduce any thrown value — axios error, fetch payload, Error, plain object —
 * to a single string that is always safe to render and to persist.
 */
export function extractErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const seen = new Set<object>();
  const anyErr = err as { response?: { data?: unknown }; message?: unknown } | null | undefined;
  // `err` itself is only worth flattening when it is the payload rather than a
  // wrapper — dumping a whole axios error would just print its internals.
  const isHttpError = typeof err === 'object' && err !== null && 'response' in err;
  const message =
    flattenErrorValue(anyErr?.response?.data, seen) ||
    flattenErrorValue(anyErr?.message, seen) ||
    (isHttpError ? '' : flattenErrorValue(err, seen)) ||
    fallback;
  return message.length > MAX_ERROR_LENGTH ? `${message.slice(0, MAX_ERROR_LENGTH)}…` : message;
}

// Chat API
export interface ChatStreamCallbacks {
  onToken: (token: string) => void;
  onDone: () => void;
  onError: (err: string) => void;
  onImage?: (data: { b64?: string; url?: string; mime?: string; alt?: string; isGenerated?: boolean }) => void;
  onThoughts?: (text: string) => void;
  onVideo?: (data: { url: string; title: string }) => void;
  onSkills?: (data: FlowkitSkillProvenanceData) => void;
  onStatus?: (text: string) => void;
  signal?: AbortSignal;
  attachedFiles?: { name: string; mime: string; b64: string }[];
}

function skillProvenance(data: Record<string, unknown>): FlowkitSkillProvenanceData {
  const { execution, loadedSkills, deferredSkills, warnings } = data;
  const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
  if (execution !== 'planning-only' || !Array.isArray(loadedSkills)
      || !loadedSkills.every(item => item && typeof item.id === 'string' && item.id.length > 0
        && typeof item.sha256 === 'string' && /^[a-f0-9]{64}$/i.test(item.sha256))
      || !strings(deferredSkills) || !strings(warnings)) {
    throw new Error('Invalid Flowkit skill provenance received; loaded sources cannot be verified.');
  }
  // Do not expose transport fields (including `type`) as provenance.
  return { execution, loadedSkills: loadedSkills.map(({ id, sha256 }) => ({ id, sha256 })), deferredSkills, warnings };
}

// LF/CRLF framing and multi-line data follow the SSE format:
// https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events#event_stream_format
// Unlike EventSource, these POST streams never reconnect or retry. Only an
// explicit done event is success, including a complete final event at EOF.
async function streamSse(path: string, input: unknown, callbacks: ChatStreamCallbacks, signal = callbacks.signal) {
  const MAX_EVENT_BYTES = 16 * 1024 * 1024; // per pending event, not the whole stream (rich images are large)
  let terminal = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const active = () => !terminal && !signal?.aborted;
  const fail = (message: string) => {
    if (!active()) return;
    terminal = true;
    callbacks.onError(message);
  };
  const cancelReader = () => { void reader?.cancel().catch(() => { /* cleanup after terminal/cancellation */ }); };
  if (!active()) return;
  signal?.addEventListener('abort', cancelReader, { once: true });

  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${useStore.getState().token}` },
      body: JSON.stringify(input),
      signal,
    });
    if (!active()) {
      void response.body?.cancel().catch(() => { /* already cancelled */ });
      return;
    }
    if (!response.ok) {
      const detail = await response.json().catch(() => null);
      fail(extractErrorMessage(detail, `HTTP ${response.status}`));
      return;
    }
    if (!response.body) throw new Error('No response stream received.');
    reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8', { fatal: true });
    const encoder = new TextEncoder();
    const separator = /\r?\n\r?\n/g;
    let buffer = '', pendingBytes = 0, searchFrom = 0;

    const dispatch = (frame: string) => {
      if (!active()) return;
      const lines = frame.split(/\r?\n/).filter(line => line === 'data' || line.startsWith('data:'));
      if (!lines.length) return; // SSE comments/heartbeat and other framing fields
      const json = lines.map(line => line.slice(5).replace(/^ /, '')).join('\n');
      let data;
      try { data = JSON.parse(json); }
      catch { throw new Error('Invalid JSON in response stream.'); }
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') {
        throw new Error('Invalid event in response stream.');
      }
      if (['token', 'thoughts', 'status'].includes(data.type) && typeof data.content !== 'string') {
        throw new Error('Invalid text in response stream.');
      }
      switch (data.type) {
        case 'done': terminal = true; callbacks.onDone(); break;
        case 'error': fail(extractErrorMessage(data.content, 'Stream failed.')); break;
        case 'token': callbacks.onToken(data.content); break;
        case 'status': callbacks.onStatus?.(data.content); break;
        case 'flowkit-skills': callbacks.onSkills?.(skillProvenance(data)); break;
        case 'image': callbacks.onImage?.(data); break;
        case 'thoughts': callbacks.onThoughts?.(data.content); break;
        case 'video': callbacks.onVideo?.(data); break;
      }
    };

    const consume = (text: string, eof: boolean) => {
      buffer += text;
      pendingBytes += encoder.encode(text).byteLength;
      separator.lastIndex = searchFrom;
      let start = 0, boundary: RegExpExecArray | null;
      while (active() && (boundary = separator.exec(buffer))) {
        const frame = buffer.slice(start, boundary.index);
        const bytes = encoder.encode(frame).byteLength;
        if (bytes > MAX_EVENT_BYTES) throw new Error('Response stream event exceeds the 16 MiB limit.');
        pendingBytes -= bytes + boundary[0].length;
        start = separator.lastIndex;
        dispatch(frame);
      }
      buffer = buffer.slice(start);
      searchFrom = Math.max(0, buffer.length - 3); // a delimiter may straddle chunks
      if (!active()) return;
      if (pendingBytes > MAX_EVENT_BYTES) throw new Error('Response stream event exceeds the 16 MiB limit.');
      if (eof && buffer) dispatch(buffer);
    };

    while (active()) {
      const { done, value } = await reader.read();
      if (!active()) break;
      consume(done ? decoder.decode() : decoder.decode(value, { stream: true }), done);
      if (done) {
        if (active()) fail('Stream ended before a done event; the response is incomplete.');
        break;
      }
    }
  } catch (cause) {
    fail(extractErrorMessage(cause, 'Connection failed.'));
  } finally {
    signal?.removeEventListener('abort', cancelReader);
    cancelReader();
    reader?.releaseLock();
  }
}

export async function streamChat(
  provider: string,
  messages: { role: string; content: string }[],
  onToken: (token: string) => void,
  onDone: () => void,
  onError: (err: string) => void,
  options?: Omit<ChatStreamCallbacks, 'onToken' | 'onDone' | 'onError'>
) {
  return streamSse('/api/chat/stream', { provider, messages, attachedFiles: options?.attachedFiles },
    { ...options, onToken, onDone, onError });
}

export interface ScriptStreamInput {
  provider: string;
  engine: Engine;
  job: PromptJob;
  hasReferences?: boolean;
}

export async function streamScript(input: ScriptStreamInput, callbacks: ChatStreamCallbacks, signal?: AbortSignal) {
  const request = input.job.characterUgc ? { ...input, job: { ...input.job,
    jobImageUrls: undefined, characterImageUrl: undefined, characterUgc: stripCharacterUgcImages(input.job.characterUgc),
  } } : input;
  return streamSse('/api/script/stream', request, callbacks, signal ?? callbacks.signal);
}

// Video API
export const videoApi = {
  health: () => api.get('/video/health'),
  listBoards: () => api.get('/video/boards'),
  createBoard: (data: any) => api.post('/video/boards', data),
  getBoard: (id: string) => api.get(`/video/boards/${id}`),
  deleteBoard: (id: string) => api.delete(`/video/boards/${id}`),
  addNode: (data: any) => api.post('/video/nodes', data),
  updateNode: (id: string, data: any) => api.patch(`/video/nodes/${id}`, data),
  deleteNode: (id: string) => api.delete(`/video/nodes/${id}`),
  addEdge: (data: any) => api.post('/video/edges', data),
  deleteEdge: (id: string) => api.delete(`/video/edges/${id}`),
  createRequest: (data: any) => api.post('/video/requests', data),
  getRequest: (id: string) => api.get(`/video/requests/${id}`),
  autoPrompt: (data: any) => api.post('/video/prompt/auto', data),
  autoPromptBatch: (data: any) => api.post('/video/prompt/auto-batch', data),
  visionDescribe: (data: any) => api.post('/video/vision/describe', data),
  chat: (data: any) => api.post('/video/chat', data),
  getBoardChat: (boardId: string) => api.get(`/video/boards/${boardId}/chat`),
  listProjects: () => api.get('/video/projects'),
  syncProjects: (data: any) => api.post('/video/projects/sync', data),
  getBoardProject: (boardId: string) => api.get(`/video/boards/${boardId}/project`),
  createBoardProject: (boardId: string, data: any) => api.post(`/video/boards/${boardId}/project`, data),
  getActivity: () => api.get('/video/activity'),
  llmProviders: () => api.get('/video/llm/providers'),
  llmConfig: () => api.get('/video/llm/config'),
};

// Code API
export const codeApi = {
  getTree: (path?: string) => api.get('/code/tree', { params: { path } }),
  getFile: (path: string) => api.get('/code/file', { params: { path } }),
  generate: (command: string, scope?: string[], provider?: string) =>
    api.post('/code/generate', { command, scope, provider }),
  getPending: () => api.get('/code/pending'),
  getPendingDetail: (id: string) => api.get(`/code/pending/${id}`),
  approve: (id: string) => api.post(`/code/approve/${id}`),
  reject: (id: string) => api.post(`/code/reject/${id}`),
  getLog: (count?: number) => api.get('/code/log', { params: { count } }),
};

// MCP API
export const mcpApi = {
  listServers: () => api.get('/mcp/servers'),
  health: (server: string) => api.get(`/mcp/health/${server}`),
  listTools: (server: string) => api.get(`/mcp/tools/${server}`),
  callTool: (server: string, tool: string, args?: any) =>
    api.post(`/mcp/call/${server}`, { tool, args }),
};

// Users API (admin only)
export interface UserRecord {
  username: string;
  role: 'admin' | 'user';
  createdAt: string;
}

export const usersApi = {
  list: () => api.get<{ users: UserRecord[] }>('/users'),
  create: (username: string, password: string, role: 'admin' | 'user') =>
    api.post('/users', { username, password, role }),
  delete: (username: string) => api.delete(`/users/${username}`),
  changePassword: (username: string, password: string) =>
    api.put(`/users/${username}/password`, { password }),
};
